import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Flyout } from '@els/els-react--flyout';
import { Card } from '@els/els-react--card';
import StructureModel from '@Components/Results/structure/structure-model.service';
import { sanitizeDiscard } from '@Services/HtmlSanitizeService';
import StructurePreview from '@Components/Results/structure/StructurePreview';
import StructureToolbarComponent from '@Components/Results/structure/structure-toolbar.component';
import InnerHTML from '@Components/InnerHTML';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import StructureContext from '@Components/Results/structure/StructureContext';

import './Structure.scss';

function getContextValue(metadata, structure) {
  return {
    metadata,
    structure,
  };
}

function getPreviewProps(structure) {
  let previewProps = {};
  const details = get(structure, 'details');

  if (details) {
    previewProps = {
      mixtureName: details.mixtureItems ? details.type : null,
      structureId: details.id,
      title: sanitizeDiscard(details.linearFormula, { allowedTags: [] }),
    };
  }

  return previewProps;
}

export default function Structure(props) {
  const { className: customClassName, isCard, metadata, showToolbar: shouldShowToolbar = true, spOptions, structure, title, zoom } = props;
  const imagePath = get(structure, 'molImage.path');
  const chemicalNames = get(structure, 'details.chemicalNames');
  const modificationLabel = get(structure, 'details.modification');
  const structureName = get(structure, 'name');
  const showToolbar = shouldShowToolbar && !!get(structure, 'details');
  const className = classnames(customClassName, 'substance-image-container');
  const structureContextValue = useMemo(() => getContextValue(metadata, structure), [metadata, structure]);
  const previewProps = useMemo(() => getPreviewProps(structure), [structure]);

  const content = (
    <div className={className} aria-label={chemicalNames?.[0] || title || ''}>
      <div className="substance-image-container-inner">
        {title && <div className="substance-image-container-title">{title}</div>}
        <StructurePreview
          zoom={zoom}
          previewPath={imagePath}
          {...previewProps}
          structureName={structureName}
          chemicalNames={chemicalNames}
        />
      </div>
      <div className="substance-toolbar-container">
        {!isEmpty(modificationLabel) && (
          <Flyout
            flyout={
              <dl>
                <dt className="u-els-font-family-bold">Modification:</dt>
                <InnerHTML tag="dd" className="modification-value" html={modificationLabel} />
              </dl>
            }
            placement="top-end"
            theme="simple"
            trigger="hover"
            pointerType="primary">
            <div className="substance-modification__content u-els-max-width-10o10">
              <InnerHTML tag="div" className="modification-value u-els-font-size-meta" html={modificationLabel} />
            </div>
          </Flyout>
        )}
        {showToolbar ? <StructureToolbarComponent spOptions={spOptions} /> : null}
      </div>
    </div>
  );

  return (
    <StructureContext.Provider value={structureContextValue}>
      {isCard ? <Card className="substance-image-card">{content}</Card> : content}
    </StructureContext.Provider>
  );
}

Structure.propTypes = {
  metadata: PropTypes.object.isRequired,
  structure: PropTypes.instanceOf(StructureModel).isRequired,
  title: PropTypes.string,
  zoom: PropTypes.number,
  spOptions: PropTypes.object,
  isCard: PropTypes.bool,
  className: PropTypes.string,
  showToolbar: PropTypes.bool,
};
